import { CallToActionCardProps } from '@molecules/*';
import { FunctionComponent } from 'react';
import { BeforeYouStartTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '@planning-breach/hooks/usePlanningBreachStore';
import { Link } from '@atoms/*';
import {
  FORM_LINK_TEXT,
  FORM_LINK_SUBTEXT,
  CHAT_LINK_TEXT,
  CHAT_LINK_SUBTEXT,
} from '@constants';

const ASSISTANT_URL = import.meta.env.VITE_ASSISTANT_URL;

const linkObjectArrays = [
  {
    level: 1,
    title: 'Home',
    href: 'https://www.westminster.gov.uk/',
  },
  {
    level: 2,
    title: 'Report it',
    href: 'https://www.westminster.gov.uk/report-it',
  },
  {
    level: 3,
    title: `Report a planning breach`,
  },
];

const typesOfIssues = [
  'unauthorised advertisements',
  'unauthorised change of use',
  'unauthorised installation of plant or equipment',
  'unauthorised works, including to listed buildings',
  'breach of planning conditions',
  'short-term lets',
];
const issueDetails = ['location of the issue', 'date the issue happened'];
const reporterDetails = [
  'any evidence you have, for example photos or documents',
  'your contact details',
];

const contentTitle = 'Report a planning breach';
const contentHeader = 'Before you start';

const ContentBody: FunctionComponent = () => {
  return (
    <>
      <h1 className="text-h1 text-brand-primary my-2">{contentHeader}</h1>
      <div className="text-bodyLg flex flex-col gap-4">
        <p>You can use this form to report:</p>
        <ul className="list-outside list-disc pl-8 leading-[28.5px]">
          {typesOfIssues.map((typesOfIssue, index) => (
            <li key={index}>{typesOfIssue}</li>
          ))}
        </ul>
        <p>To help resolve the issue, we need to know the:</p>
        <ul className="list-outside list-disc pl-8 leading-[28.5px]">
          {issueDetails.map((issueDetail, index) => (
            <li key={index}>{issueDetail}</li>
          ))}
        </ul>
        <p>It’s useful to provide:</p>
        <ul className="list-outside list-disc pl-8 leading-[28.5px]">
          {reporterDetails.map((reporterDetail, index) => (
            <li key={index}>{reporterDetail}</li>
          ))}
        </ul>
      </div>
    </>
  );
};

export const BeforeYouStart: FunctionComponent = () => {
  const navigate = useNavigate();
  const sessionId = uuidv4();
  const { reset, update } = usePlanningBreachStore();
  const initiateForm = () => {
    reset();
    update('sessionId', sessionId);
    navigate(RoutesList.ISSUE_TYPE);
  };

  const reportItCTA: CallToActionCardProps = {
    linkText: FORM_LINK_TEXT,
    linkSubtext: FORM_LINK_SUBTEXT,
    onClick: initiateForm,
  };

  const reportItAssistantRoute = `${ASSISTANT_URL}?category=planning_breach`;
  const reportItAssistantCTA: CallToActionCardProps = {
    linkText: CHAT_LINK_TEXT,
    linkSubtext: CHAT_LINK_SUBTEXT,
    externalHref: reportItAssistantRoute,
  };

  const callToActionCards = [reportItAssistantCTA, reportItCTA];

  const PlanningLink: FunctionComponent = () => {
    return (
      <div className="text-bodyLg">
        <p>
          For more information about planning please visit:{' '}
          <Link
            href="https://www.westminster.gov.uk/planning-building-control-and-environmental-regulations"
            className="text-action-link font-normal underline"
            linkType="inline"
            external={true}
          >
            https://www.westminster.gov.uk/planning-building-control-and-environmental-regulations
          </Link>
        </p>
      </div>
    );
  };

  return (
    <BeforeYouStartTemplate
      contentTitle={contentTitle}
      callToActionCards={callToActionCards}
      linkObjectArrays={linkObjectArrays}
      contentSubtext={<ContentBody />}
      additionalContent={<PlanningLink />}
    />
  );
};
